import { z } from 'zod'

/**
 * Der Wartungsbericht für eine Gruppe (Feature „Wartungsbericht pro Kunde", 04.09.2026).
 *
 * Er beantwortet die Frage, die am Monatsende kommt: „Was hast du eigentlich gemacht?" Alles
 * darin steht ohnehin schon im Audit-Log, in den Backup-Läufen und in den Alarmen — der Bericht
 * fasst es nur für einen Zeitraum und eine Gruppe zusammen, statt es neu zu erheben.
 *
 * Als „Kunde" dient die Gruppe des Servers. Ein zweites Feld dafür wäre eine zweite Wahrheit, die
 * gepflegt werden müsste; die Gruppe ist schon da und wird schon benutzt.
 */
export const reportActivitySchema = z.object({
  /** Fachlicher Sammelbegriff, nicht die technische Aktion — „Updates eingespielt" statt `updates.run`. */
  label: z.string(),
  count: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
})
export type ReportActivity = z.infer<typeof reportActivitySchema>

export const reportServerSchema = z.object({
  id: z.string(),
  name: z.string(),
  host: z.string(),
  /** Aus dem letzten Messpunkt im Zeitraum; null, wenn nichts gemessen wurde. */
  diskUsedPct: z.number().min(0).max(100).nullable(),
  measurements: z.number().int().nonnegative(),
})
export type ReportServer = z.infer<typeof reportServerSchema>

export const reportIncidentSchema = z.object({
  at: z.iso.datetime(),
  serverName: z.string().nullable(),
  title: z.string(),
  message: z.string(),
  /** Wann er sich erledigt hat; null, wenn er im Zeitraum offen geblieben ist. */
  resolvedAt: z.iso.datetime().nullable(),
})
export type ReportIncident = z.infer<typeof reportIncidentSchema>

export const maintenanceReportSchema = z.object({
  group: z.string(),
  from: z.iso.datetime(),
  /** Ausschließlich — der Zeitraum ist [from, to). */
  to: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  servers: z.array(reportServerSchema),
  activities: z.array(reportActivitySchema),
  incidents: z.array(reportIncidentSchema),
  backups: z.object({
    ok: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /** Summe der Archivgrößen der erfolgreichen Läufe. */
    bytes: z.number().int().nonnegative(),
  }),
  /** Alarme, die am Ende des Zeitraums noch offen waren. */
  stillOpen: z.number().int().nonnegative(),
})
export type MaintenanceReport = z.infer<typeof maintenanceReportSchema>

export const maintenanceReportQuerySchema = z.object({
  group: z.string().trim().min(1, 'Bitte wähle eine Gruppe.'),
  from: z.iso.datetime(),
  to: z.iso.datetime(),
})
